/* Mayfly Studios — animation layer v2.
 * Everything routes through window.mfGSAP() (mf-gsap-init.js): scoped via
 * gsap.context() so it can be reverted, and when prefers-reduced-motion is on
 * the setup fn is called once with { reduced: true } and no tween is created.
 *
 * FAIL-SAFE RULE: never use gsap.from() for anything that hides content.
 * Elements sit at natural opacity; entrances run inside onEnter via fromTo().
 * If a trigger never fires, content is simply visible — never stranded.
 *
 * Core GSAP + ScrollTrigger only. FLIP is hand-rolled so there is no dependency
 * on plugin CDN availability.
 *
 * Transform ownership: mayfly.js previously wrote .style.transform on the hero
 * slides/plate and the prism on an interval. Those blocks were removed from
 * mayfly.js so GSAP is now the sole owner of those properties.
 */
(function () {
  if (typeof window === 'undefined') return;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    if (!window.gsap || !window.mfGSAP) {
      console.warn('[mf-anim] gsap/mfGSAP not ready — skipping');
      return;
    }

    var contexts = [];
    function track(c) { if (c) contexts.push(c); return c; }

    var hasST = !!window.ScrollTrigger;
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var isDesktop = window.matchMedia('(min-width: 768px)').matches;
    var reduced = !!window.mfReducedMotion;
    var qs = function (s, c) { return (c || document).querySelector(s); };
    var qsa = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

    function revealOnce(targets, trigger, stagger) {
      if (!hasST) return;
      ScrollTrigger.create({
        trigger: trigger, start: 'top 85%', once: true,
        onEnter: function () {
          gsap.fromTo(targets, { opacity: 0, y: 16 },
            { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: stagger || 0, clearProps: 'transform' });
        }
      });
    }

    /* 1. Header badge spin */
    var badge = qs('.mf-badge-spin');
    if (badge) {
      track(window.mfGSAP(badge, function (o) {
        if (o.reduced) { gsap.set(badge, { rotationY: 0 }); return; }
        gsap.to(badge, { rotationY: 360, duration: 7, ease: 'none', repeat: -1, transformOrigin: '50% 50%' });
      }));
    }

    /* 2. Marquee — reacts to scroll velocity and direction */
    var marquee = qs('.mf-marquee-track');
    if (marquee) {
      track(window.mfGSAP(marquee, function (o) {
        if (o.reduced) { gsap.set(marquee, { xPercent: 0 }); return; }
        var loop = gsap.fromTo(marquee, { xPercent: 0 },
          { xPercent: -50, duration: 26, ease: 'none', repeat: -1 });
        if (!hasST) return;
        var settle;
        var st = ScrollTrigger.create({
          onUpdate: function (self) {
            var v = self.getVelocity();
            if (!v) return;
            var dir = v < 0 ? -1 : 1;
            var boost = 1 + Math.min(Math.abs(v) / 700, 5);
            gsap.to(loop, { timeScale: boost * dir, duration: 0.25, overwrite: true });
            clearTimeout(settle);
            settle = setTimeout(function () {
              gsap.to(loop, { timeScale: dir, duration: 0.8, ease: 'power2.out', overwrite: true });
            }, 180);
          }
        });
        return function () { clearTimeout(settle); st.kill(); };
      }));
    }

    /* 3. Scroll reveals */
    [qs('[data-mf-grid]'), qs('#basics')].filter(Boolean).forEach(function (group) {
      var cards = group.querySelectorAll('[data-mf-product]');
      if (!cards.length) return;
      track(window.mfGSAP(group, function (o) {
        if (o.reduced) { gsap.set(cards, { opacity: 1, y: 0 }); return; }
        revealOnce(cards, group, { each: 0.06, from: 'start' });
      }));
    });

    var revealables = qsa('[data-mf-reveal]');
    if (revealables.length) {
      track(window.mfGSAP(document.body, function (o) {
        if (o.reduced) { gsap.set(revealables, { opacity: 1, y: 0 }); return; }
        revealables.forEach(function (el) { revealOnce(el, el, 0); });
      }));
    }

    /* 4. Hero — copy entrance, scroll parallax, pointer tilt */
    var heroCopy = qs('[data-mf-hero-copy][data-index="0"]');
    if (heroCopy && heroCopy.children.length) {
      track(window.mfGSAP(heroCopy, function (o) {
        if (o.reduced) { gsap.set(heroCopy.children, { opacity: 1, y: 0 }); return; }
        gsap.fromTo(heroCopy.children, { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.09, delay: 0.15, clearProps: 'transform' });
      }));
    }

    var heroSection = qs('[data-mf-hero]');
    var heroSlides = qsa('[data-mf-hero-slide]');
    var heroPlate = qs('[data-mf-hero-plate]');

    if (heroSection && heroSlides.length) {
      track(window.mfGSAP(heroSection, function (o) {
        if (o.reduced) {
          gsap.set(heroSlides, { scale: 1, x: 0, y: 0, yPercent: 0 });
          if (heroPlate) gsap.set(heroPlate, { rotationX: 0, rotationY: 0 });
          return;
        }
        gsap.set(heroSlides, { scale: 1.06 });
        if (hasST) {
          gsap.to(heroSlides, {
            yPercent: 6, ease: 'none',
            scrollTrigger: { trigger: heroSection, start: 'top top', end: 'bottom top', scrub: true }
          });
        }
        if (!canHover) return;
        var sx = gsap.quickTo(heroSlides, 'x', { duration: 0.7, ease: 'power2.out' });
        var sy = gsap.quickTo(heroSlides, 'y', { duration: 0.7, ease: 'power2.out' });
        var px = heroPlate && gsap.quickTo(heroPlate, 'rotationY', { duration: 0.7, ease: 'power2.out' });
        var py = heroPlate && gsap.quickTo(heroPlate, 'rotationX', { duration: 0.7, ease: 'power2.out' });
        if (heroPlate) gsap.set(heroPlate, { transformPerspective: 1200 });
        function onMove(e) {
          var tiltY = (e.clientX / window.innerWidth - 0.5) * 10;
          var tiltX = -(e.clientY / window.innerHeight - 0.5) * 6;
          sx(tiltY * -1.2); sy(tiltX * -1.2);
          if (px) { px(tiltY * 0.35); py(tiltX * 0.35); }
        }
        window.addEventListener('pointermove', onMove);
        return function () { window.removeEventListener('pointermove', onMove); };
      }));
    }

    /* 5. Prism — GSAP-owned auto-spin, drag and inertia */
    var prism = qs('[data-mf-prism]');
    var prismWrap = qs('[data-mf-prism-wrap]');
    if (prism && prismWrap) {
      track(window.mfGSAP(prismWrap, function (o) {
        var state = { v: 0 };
        function render() {
          prism.style.transform = 'translateZ(-320px) rotateX(-4deg) rotateY(' + state.v.toFixed(2) + 'deg)';
        }
        render();
        if (o.reduced) return;

        var AUTO = 0.22, momentum = 0, dragging = false, lastX = 0, lastT = 0;
        function tick() {
          if (dragging) return;
          state.v += AUTO + momentum;
          momentum *= 0.94;
          if (Math.abs(momentum) < 0.001) momentum = 0;
          render();
        }
        gsap.ticker.add(tick);

        function down(e) {
          dragging = true; momentum = 0;
          lastX = e.clientX; lastT = performance.now();
          prismWrap.style.cursor = 'grabbing';
          if (prismWrap.setPointerCapture) { try { prismWrap.setPointerCapture(e.pointerId); } catch (err) {} }
        }
        function move(e) {
          if (!dragging) return;
          var dx = e.movementX || (e.clientX - lastX);
          var now = performance.now();
          var dt = Math.max(now - lastT, 1);
          state.v += dx * 0.35;
          momentum = (dx * 0.35) / dt * 16;
          lastX = e.clientX; lastT = now;
          render();
        }
        function up() {
          if (!dragging) return;
          dragging = false;
          prismWrap.style.cursor = 'grab';
          momentum = Math.max(Math.min(momentum, 12), -12);
        }
        prismWrap.addEventListener('pointerdown', down);
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);

        return function () {
          gsap.ticker.remove(tick);
          prismWrap.removeEventListener('pointerdown', down);
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
        };
      }));
    }

    /* 6. Product card hover lift */
    if (canHover) {
      qsa('[data-mf-product]').forEach(function (card) {
        var photo = qs('[data-mf-photo]', card) || qs('[data-mf-blank-block]', card);
        if (!photo) return;
        track(window.mfGSAP(card, function (o) {
          if (o.reduced) return;
          var toScale = gsap.quickTo(photo, 'scale', { duration: 0.35, ease: 'power2.out' });
          var toY = gsap.quickTo(card, 'y', { duration: 0.35, ease: 'power2.out' });
          function enter() { toScale(1.03); toY(-4); }
          function leave() { toScale(1); toY(0); }
          card.addEventListener('mouseenter', enter);
          card.addEventListener('mouseleave', leave);
          card.addEventListener('focusin', enter);
          card.addEventListener('focusout', leave);
          return function () {
            card.removeEventListener('mouseenter', enter);
            card.removeEventListener('mouseleave', leave);
            card.removeEventListener('focusin', enter);
            card.removeEventListener('focusout', leave);
          };
        }));
      });
    }

    /* 7. FLIP grid filtering (hand-rolled). mf-animations.js loads before
     * mayfly.js so our listener runs first: measure First, let mayfly.js mutate
     * display, then measure Last next frame and invert. */
    var gridEl = qs('[data-mf-grid]');
    if (gridEl) {
      track(window.mfGSAP(gridEl, function (o) {
        if (o.reduced) return;
        var cards = qsa('[data-mf-product]', gridEl);
        function flip() {
          var first = cards.map(function (c) {
            var r = c.getBoundingClientRect();
            return { el: c, top: r.top, left: r.left, shown: r.width > 0 };
          });
          requestAnimationFrame(function () {
            first.forEach(function (f) {
              var r = f.el.getBoundingClientRect();
              if (!(r.width > 0)) return;
              if (!f.shown) {
                gsap.fromTo(f.el, { opacity: 0, scale: 0.96 },
                  { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out', clearProps: 'transform' });
                return;
              }
              var dx = f.left - r.left, dy = f.top - r.top;
              if (!dx && !dy) return;
              gsap.fromTo(f.el, { x: dx, y: dy },
                { x: 0, y: 0, duration: 0.5, ease: 'power2.out', clearProps: 'transform' });
            });
          });
        }
        var triggers = qsa('[data-mf-tab], [data-mf-collection-pick]');
        triggers.forEach(function (t) { t.addEventListener('click', flip); });
        var search = qs('[data-mf-search-input]');
        if (search) search.addEventListener('input', flip);
        return function () {
          triggers.forEach(function (t) { t.removeEventListener('click', flip); });
          if (search) search.removeEventListener('input', flip);
        };
      }));
    }

    /* 8. Add-to-bag flight + bag badge pop */
    function popBag(bag) {
      gsap.fromTo(bag, { scale: 1 },
        { scale: 1.25, duration: 0.16, ease: 'power2.out', yoyo: true, repeat: 1, transformOrigin: '50% 50%' });
    }
    function flyToBag(sourceImg) {
      var bag = qs('[data-mf-bag-open]');
      if (!bag) return;
      if (reduced || !sourceImg) { popBag(bag); return; }
      var r = sourceImg.getBoundingClientRect();
      var br = bag.getBoundingClientRect();
      if (!r.width) { popBag(bag); return; }
      var clone = sourceImg.cloneNode(false);
      clone.style.cssText = 'position:fixed;left:' + r.left + 'px;top:' + r.top + 'px;width:' +
        r.width + 'px;height:' + r.height + 'px;object-fit:cover;z-index:9999;pointer-events:none;border-radius:4px;margin:0';
      document.body.appendChild(clone);
      gsap.to(clone, {
        left: br.left + br.width / 2 - 18,
        top: br.top + br.height / 2 - 18,
        width: 36, height: 36, opacity: 0.15,
        duration: 0.75, ease: 'power2.inOut',
        onComplete: function () { clone.remove(); popBag(bag); }
      });
    }

    var pdpAdd = qs('[data-mf-pdp-add]');
    if (pdpAdd) {
      track(window.mfGSAP(pdpAdd, function () {
        function onAdd() { flyToBag(qs('[data-mf-pdp-photo]')); }
        pdpAdd.addEventListener('click', onAdd);
        return function () { pdpAdd.removeEventListener('click', onAdd); };
      }));
    }

    /* 9. Sticky buy bar (PDP) — built in JS, no Liquid change needed */
    var pdpRoot = qs('[data-mf-pdp]');
    if (pdpRoot && pdpAdd && hasST) {
      track(window.mfGSAP(pdpRoot, function () {
        var titleEl = qs('h1', pdpRoot) || qs('h1');
        var priceEl = qs('[data-mf-pdp-price]');
        if (!titleEl) return;

        var bar = document.createElement('div');
        bar.setAttribute('data-mf-sticky-bar', '');
        bar.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:45;background:#ffffff;' +
          'border-top:1px solid #000000;padding:12px 16px;display:flex;align-items:center;gap:16px;' +
          'justify-content:space-between';
        bar.innerHTML =
          '<div style="display:flex;flex-direction:column;gap:2px;min-width:0">' +
            '<span style="font-size:13px;font-weight:400;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' +
              titleEl.textContent.trim() + '</span>' +
            '<span data-mf-sticky-price style="font-size:13px;font-weight:500;line-height:1.3">' +
              (priceEl ? priceEl.textContent.trim() : '') + '</span>' +
          '</div>' +
          '<button type="button" data-mf-sticky-add class="mf-hover-invert" style="flex:none;background:#000000;' +
          'border:1px solid #000000;border-radius:4px;padding:12px 24px;min-height:44px;font-size:12px;font-weight:500;' +
          'letter-spacing:0.1em;text-transform:uppercase;color:#ffffff">Add to bag</button>';
        document.body.appendChild(bar);
        gsap.set(bar, { yPercent: 110 });

        var proxy = qs('[data-mf-sticky-add]', bar);
        function onProxy() { pdpAdd.click(); }
        proxy.addEventListener('click', onProxy);

        var priceSync = null;
        if (priceEl && window.MutationObserver) {
          var mirror = qs('[data-mf-sticky-price]', bar);
          priceSync = new MutationObserver(function () { mirror.textContent = priceEl.textContent.trim(); });
          priceSync.observe(priceEl, { childList: true, characterData: true, subtree: true });
        }

        var shown = false;
        function setShown(v) {
          if (v === shown) return;
          shown = v;
          if (reduced) { gsap.set(bar, { yPercent: v ? 0 : 110 }); return; }
          gsap.to(bar, { yPercent: v ? 0 : 110, duration: 0.4, ease: 'power3.out' });
        }

        var st = ScrollTrigger.create({
          trigger: pdpAdd, start: 'bottom top',
          onEnter: function () { setShown(true); },
          onLeaveBack: function () { setShown(false); }
        });

        return function () {
          st.kill();
          proxy.removeEventListener('click', onProxy);
          if (priceSync) priceSync.disconnect();
          bar.remove();
        };
      }));
    }

    /* 10. Colour swatch morph — crossfade instead of hard src swap */
    function bindSwatchMorph(selector, photoSelector) {
      qsa(selector).forEach(function (btn) {
        track(window.mfGSAP(btn, function (o) {
          if (o.reduced) return;
          function onClick() {
            var scope = btn.closest('[data-mf-product]') || document;
            var photo = qs(photoSelector, scope);
            if (!photo) return;
            var r = photo.getBoundingClientRect();
            if (!r.width) return;
            var ghost = photo.cloneNode(false);
            ghost.removeAttribute('data-mf-photo');
            ghost.removeAttribute('data-mf-pdp-photo');
            ghost.style.cssText = 'position:fixed;left:' + r.left + 'px;top:' + r.top + 'px;width:' + r.width +
              'px;height:' + r.height + 'px;object-fit:cover;z-index:5;pointer-events:none;margin:0';
            document.body.appendChild(ghost);
            gsap.to(ghost, { opacity: 0, duration: 0.45, ease: 'power2.out',
              onComplete: function () { ghost.remove(); } });
            gsap.fromTo(photo, { opacity: 0.35 }, { opacity: 1, duration: 0.45, ease: 'power2.out' });
          }
          btn.addEventListener('click', onClick);
          return function () { btn.removeEventListener('click', onClick); };
        }));
      });
    }
    bindSwatchMorph('[data-mf-swatch]', '[data-mf-photo]');
    bindSwatchMorph('[data-mf-pdp-color]', '[data-mf-pdp-photo]');

    /* 11. Cart drawer — staggered line items on open */
    var drawer = qs('[data-mf-bag-drawer]');
    if (drawer && window.MutationObserver) {
      track(window.mfGSAP(drawer, function (o) {
        if (o.reduced) return;
        var obs = new MutationObserver(function () {
          if (!drawer.classList.contains('is-open')) return;
          var items = qsa('[data-mf-bag-items] > div', drawer);
          if (!items.length) return;
          gsap.fromTo(items, { opacity: 0, x: 18 },
            { opacity: 1, x: 0, duration: 0.4, ease: 'power2.out', stagger: 0.06, clearProps: 'transform' });
        });
        obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        return function () { obs.disconnect(); };
      }));
    }

    /* 12. Custom cursor — desktop pointer devices only */
    if (canHover && isDesktop) {
      track(window.mfGSAP(document.body, function (o) {
        if (o.reduced) return;
        var dot = document.createElement('div');
        dot.setAttribute('data-mf-cursor', '');
        dot.style.cssText = 'position:fixed;top:0;left:0;width:36px;height:36px;border:1px solid #ffffff;' +
          'border-radius:9999px;pointer-events:none;z-index:9998;opacity:0;display:flex;align-items:center;' +
          'justify-content:center;font-size:8px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;' +
          'mix-blend-mode:difference;color:#ffffff';
        document.body.appendChild(dot);
        gsap.set(dot, { xPercent: -50, yPercent: -50 });
        var tx = gsap.quickTo(dot, 'x', { duration: 0.35, ease: 'power3.out' });
        var ty = gsap.quickTo(dot, 'y', { duration: 0.35, ease: 'power3.out' });
        var shown = false;
        function onMove(e) {
          tx(e.clientX); ty(e.clientY);
          if (!shown) { shown = true; gsap.to(dot, { opacity: 1, duration: 0.3 }); }
        }
        function onLeave() { shown = false; gsap.to(dot, { opacity: 0, duration: 0.2 }); }
        var cards = qsa('[data-mf-product]');
        function grow() { gsap.to(dot, { scale: 2.1, duration: 0.3, ease: 'power3.out' }); dot.textContent = 'View'; }
        function shrink() { gsap.to(dot, { scale: 1, duration: 0.3, ease: 'power3.out' }); dot.textContent = ''; }
        cards.forEach(function (c) {
          c.addEventListener('mouseenter', grow);
          c.addEventListener('mouseleave', shrink);
        });
        window.addEventListener('pointermove', onMove);
        document.addEventListener('mouseleave', onLeave);
        return function () {
          window.removeEventListener('pointermove', onMove);
          document.removeEventListener('mouseleave', onLeave);
          cards.forEach(function (c) {
            c.removeEventListener('mouseenter', grow);
            c.removeEventListener('mouseleave', shrink);
          });
          dot.remove();
        };
      }));
    }

    /* 13. PDP photo tilt */
    var pdpPhoto = qs('[data-mf-pdp-photo]');
    if (pdpPhoto && canHover) {
      var pdpWrap = pdpPhoto.parentElement;
      track(window.mfGSAP(pdpWrap, function (o) {
        if (o.reduced) { gsap.set(pdpPhoto, { rotationX: 0, rotationY: 0 }); return; }
        gsap.set(pdpWrap, { perspective: 900 });
        var rx = gsap.quickTo(pdpPhoto, 'rotationX', { duration: 0.5, ease: 'power2.out' });
        var ry = gsap.quickTo(pdpPhoto, 'rotationY', { duration: 0.5, ease: 'power2.out' });
        function onMove(e) {
          var r = pdpWrap.getBoundingClientRect();
          ry(((e.clientX - r.left) / r.width - 0.5) * 7);
          rx(-((e.clientY - r.top) / r.height - 0.5) * 7);
        }
        function onLeave() { rx(0); ry(0); }
        pdpWrap.addEventListener('mousemove', onMove);
        pdpWrap.addEventListener('mouseleave', onLeave);
        return function () {
          pdpWrap.removeEventListener('mousemove', onMove);
          pdpWrap.removeEventListener('mouseleave', onLeave);
        };
      }));
    }

    if (hasST) {
      window.addEventListener('load', function () { ScrollTrigger.refresh(); });
      setTimeout(function () { ScrollTrigger.refresh(); }, 1200);
    }

    window.mfAnimations = {
      contexts: contexts,
      revertAll: function () { contexts.forEach(function (c) { c && c.revert && c.revert(); }); }
    };

    console.log('[mf-anim v2] ' + contexts.length + ' contexts | reduced: ' + reduced +
      ' | hover: ' + canHover + ' | desktop: ' + isDesktop);
  });
})();
